import queryString from "query-string";
import { AxiosInstance } from "axios";
import useAuthenticatedFetch from "../useAuthenticatedFetch";
import {
  CommonContactResponse,
  ContactResponse,
  ContactDetails,
  IContactsReorder,
  IScheduleItem,
  IContactsRelation,
  ICommonContacts,
  CheckContactExists,
  IAssociatedObjects,
} from "./types";

const useCommonContacts = (params: {
  search?: string;
  page?: number;
  pageSize?: number;
  organizationIds?: string;
}) => {
  const settings = queryString.stringify({
    ...params,
  });

  return useAuthenticatedFetch<CommonContactResponse[]>(
    `/api/v2/contacts?${settings}`
  );
};

const useCommonContactsForUser = (
  userId: string,
  params: { page: number; pageSize: number; search: string }
) => {
  const settings = queryString.stringify({
    contactType: 2,
    ...params,
  });

  return useAuthenticatedFetch<ContactResponse[]>(
    `/api/v2/users/${userId}/contacts?${settings}`
  );
};

const useContactDetails = (contactId: number, objectId?: string) => {
  const settings = queryString.stringify({
    userId: objectId,
  });

  return useAuthenticatedFetch<ContactDetails>(
    `/api/v2/contacts/${contactId}?${settings}`
  );
};

const useUserContactDetails = (contactId: number, objectId?: string) =>
  useAuthenticatedFetch<ContactDetails>(
    `/api/v2/users/${objectId}/contacts/${contactId}`
  );

const getCommonContacts = async (
  params: {
    search?: string;
    page?: number;
    pageSize?: number;
    organizationIds?: string;
  },
  authenticatedRequest: AxiosInstance
) => {
  const settings = queryString.stringify({
    ...params,
  });

  return authenticatedRequest.get<ICommonContacts>(
    `/api/v2/contacts/table?${settings}`
  );
};

const getContactDetails = async (
  id: string,
  authenticatedRequest: AxiosInstance
) => authenticatedRequest.get<ContactDetails>(`/api/v2/contacts/${id}`);

const getUserContacts = async (
  objectId: string,
  authenticatedRequest: AxiosInstance,
  page?: number
) =>
  authenticatedRequest.get<ContactResponse[]>(
    `/api/v2/users/${objectId}/contacts?contactType=1${
      page ? `&page=${page}&pageSize=15` : ""
    }`
  );

const editContact = async (
  {
    contactId,
    contactDetails,
  }: {
    contactId: number;
    contactDetails: ContactDetails;
  },
  authenticatedRequest: AxiosInstance
) => {
  const requestData = JSON.stringify(contactDetails);

  return authenticatedRequest.put<{}>(
    `/api/v2/contacts/${contactId}`,
    requestData,
    { headers: { "Content-Type": "application/json" } }
  );
};

const deletePrivateContact = async (
  objectId: string,
  contactId: number,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.delete<{}>(
    `/api/v2/users/${objectId}/contacts/${contactId}`
  );

const associateCommonContact = (
  objectId: string,
  contactId: number,
  values: IContactsRelation,
  authenticatedRequest: AxiosInstance
) => {
  const settings = queryString.stringify(values);
  return authenticatedRequest.post<{}>(
    `/api/v2/users/${objectId}/contacts/${contactId}/associate?${settings}`
  );
};

const updateAssociationCommonContact = (
  objectId: string,
  contactId: number,
  values: IContactsRelation,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.put<{}>(
    `/api/v2/users/${objectId}/contacts/${contactId}/associate?`,
    values
  );

const unassociateCommonContact = (
  objectId: string,
  contactId: number,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.delete<{}>(
    `/api/v2/users/${objectId}/contacts/${contactId}/associate`
  );

const createCommonContact = async (
  contactDetails: ContactDetails,
  authenticatedRequest: AxiosInstance
) => {
  const requestData = JSON.stringify(contactDetails);

  return authenticatedRequest.post<any>(`/api/v2/contacts`, requestData, {
    headers: { "Content-Type": "application/json" },
  });
};

const checkCommonContactExists = async (
  exists: CheckContactExists,
  authenticatedRequest: AxiosInstance
) => {
  const settings = queryString.stringify(exists);

  return authenticatedRequest.get<CheckContactExists>(
    `/api/v2/contacts/exists?${settings}`
  );
};

const deleteCommonContact = (
  contactId: number,
  authenticatedRequest: AxiosInstance
) => authenticatedRequest.delete<{}>(`/api/v2/contacts/${contactId}`);

const createContactCredentails = (
  contactId: number,
  authenticatedRequest: AxiosInstance,
  isPrivate?: boolean,
  userId?: string
) =>
  authenticatedRequest.post(
    `/api/v2/contacts/${contactId}/resource${
      userId && isPrivate ? `?userId=${userId}` : ""
    }`
  );

const reorderContactsPriotiry = (
  objectId: string,
  contacts: IContactsReorder,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.put(
    `/api/v2/users/${objectId}/contacts/order`,
    JSON.stringify({ contactPriorities: contacts }),
    {
      headers: { "Content-Type": "application/json" },
    }
  );

const getScheduleItems = (
  contactId: number,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.get<IScheduleItem[]>(
    `/api/v2/contacts/${contactId}/scheduleitems`
  );

const updateScheduleItem = (
  contactId: number,
  values: IScheduleItem,
  authenticatedRequest: AxiosInstance
) => {
  const methodType = values.id ? "put" : "post";
  return authenticatedRequest[methodType](
    `/api/v2/contacts/${contactId}/scheduleitem`,
    JSON.stringify(values),
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

const deleteScheduleItem = (
  contactId: number,
  scheduleItemId: number,
  authenticatedRequest: AxiosInstance
) =>
  authenticatedRequest.delete(
    `/api/v2/contacts/${contactId}/scheduleitem/${scheduleItemId}`
  );

const fetchAssociatedObjects = (
  contactId: number,
  authenticatedRequest: AxiosInstance,
  params: {
    page: number;
    pageSize: number;
  }
) => {
  const settings = queryString.stringify({
    ...params,
  });
  return authenticatedRequest.get<IAssociatedObjects>(
    `/api/v2/contacts/${contactId}/users/table?${settings}`
  );
};

const contactsBulkDelete = async (
  deleteData: { limit: number; contactIds: string[] },
  authenticatedRequest: AxiosInstance
) =>
  await authenticatedRequest.delete(`/api/v2/contacts/delete/batch`, {
    data: JSON.stringify(deleteData),
    headers: { "Content-Type": "application/json" },
  });

export {
  useCommonContacts,
  useCommonContactsForUser,
  useContactDetails,
  getUserContacts,
  getCommonContacts,
  getContactDetails,
  editContact,
  deletePrivateContact,
  associateCommonContact as addCommonContact,
  updateAssociationCommonContact,
  unassociateCommonContact,
  createCommonContact,
  deleteCommonContact,
  createContactCredentails,
  useUserContactDetails,
  reorderContactsPriotiry,
  getScheduleItems,
  updateScheduleItem,
  deleteScheduleItem,
  checkCommonContactExists,
  fetchAssociatedObjects,
  contactsBulkDelete,
};
